import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../utils/csrf';
import { CashflowHistoryItem } from '../models/ModelCashflow';

const formatDate = (date: string) => {
  // Ensure date is in YYYY-MM format
  const parsedDate = new Date(date);
  return `${parsedDate.getUTCFullYear()}-${String(parsedDate.getUTCMonth() + 1).padStart(2, '0')}`;
};

export const useEditCashflow = () => {
  const navigate = useNavigate();
  const [cashflowHistory, setCashflowHistory] = useState<CashflowHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchCashflowData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const csrfToken = await getCsrfToken();
      const response = await fetch('/api/cashflow/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });

      if (response.redirected) {
        window.location.href = '/login';
        return;
      }

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 400) {
          if (result.error === 'No income data found') {
            setError('income');
            navigate('/income');
            return;
          }
          if (result.error === 'No expense data found') {
            setError('expense');
            navigate('/expenses');
            return;
          }
        }
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      
      if (!result.success || !result.has_cashflow_data) {
        navigate('/cashflow');
        return;
      }
      
      const sortedHistory = [...result.data.cashflow_history].map(item => ({
        ...item,
        others: item.others || 0 // Ensure the field exists with default value
      })).sort(
        (a: CashflowHistoryItem, b: CashflowHistoryItem) => 
        new Date(a.month).getTime() - new Date(b.month).getTime()
      );
      
      setCashflowHistory(sortedHistory);
    } catch (error) {
      console.error('Error fetching cashflow data:', error);
      if (error instanceof Error && error.message === 'Failed to fetch CSRF token') {
        window.location.href = '/login';
        return;
      }
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const updateCashflowData = useCallback(async (updatedHistory: CashflowHistoryItem[]) => {
    try {
      setIsSaving(true);
      setError(null);
      const csrfToken = await getCsrfToken();
      
      // Format the dates consistently before sending to backend
      const formattedHistory = updatedHistory.map(item => ({
        ...item,
        month: formatDate(item.month),
        additional_cashflow: item.others || 0 // Map others to additional_cashflow for backend
      }));

      const response = await fetch('/api/cashflow/edit/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({ cashflow_history: formattedHistory }),
      });

      if (response.redirected) {
        window.location.href = '/login';
        return;
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      setCashflowHistory(updatedHistory);
      return true;
    } catch (error) {
      console.error('Error updating cashflow data:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while updating data');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const handleFieldUpdate = useCallback((monthIndex: number, field: keyof CashflowHistoryItem, value: number) => {
    setCashflowHistory(prevHistory => {
      const newHistory = [...prevHistory];
      newHistory[monthIndex] = {
        ...newHistory[monthIndex],
        [field]: value
      };
      return newHistory;
    });
  }, []);

  return {
    cashflowHistory,
    fetchCashflowData,
    updateCashflowData,
    handleFieldUpdate,
    isLoading,
    isSaving,
    error,
  };
};
